import { crDateOf, daysBetween, periodLabel, parsePeriodKey } from "@despejo/domain";
import { pendingAction } from "@despejo/compliance-engine";
import type { ObligationRow, StatusRow } from "./types.js";

export type Section = "OVERDUE" | "THIS_WEEK" | "UPCOMING";

export interface DashboardItem {
  row: ObligationRow;
  section: Section;
  daysLeft: number;
  pending: "FILING" | "PAYMENT";
}

export interface Dashboard {
  overdue: DashboardItem[];
  thisWeek: DashboardItem[];
  upcoming: DashboardItem[];
  counts: { overdue: number; thisWeek: number; upcoming: number };
}

/**
 * Plan §9.1: everything still to do, grouped by urgency in Costa Rica time.
 * UPCOMING is limited to `horizonDays` so the list stays actionable.
 */
export function buildDashboard(rows: readonly ObligationRow[], now: Date, horizonDays = 45): Dashboard {
  const today = crDateOf(now);
  const items: DashboardItem[] = [];
  for (const row of rows) {
    const pending = pendingAction(row);
    if (!pending) continue;
    const daysLeft = daysBetween(today, row.due_date);
    const overdue = row.is_overdue_filing || row.is_overdue_payment || now.getTime() > Date.parse(row.due_at);
    let section: Section | null = null;
    if (overdue) section = "OVERDUE";
    else if (daysLeft <= 7) section = "THIS_WEEK";
    else if (daysLeft <= horizonDays) section = "UPCOMING";
    if (section) items.push({ row, section, daysLeft, pending });
  }
  const order = (a: DashboardItem, b: DashboardItem) =>
    a.row.due_date.localeCompare(b.row.due_date) || a.row.taxpayer_name.localeCompare(b.row.taxpayer_name) || a.row.sort_order - b.row.sort_order;
  const pick = (s: Section) => items.filter((i) => i.section === s).sort(order);
  // Overdue: most recent first — years of unfiled history must not bury last month's return.
  const overdue = pick("OVERDUE").reverse();
  const thisWeek = pick("THIS_WEEK");
  const upcoming = pick("UPCOMING");
  return { overdue, thisWeek, upcoming, counts: { overdue: overdue.length, thisWeek: thisWeek.length, upcoming: upcoming.length } };
}

/** "septiembre 2026", "T3 2026", "2026 · cuota P2". */
export function periodText(key: string): string {
  const [base, inst] = key.split(":");
  try {
    const label = periodLabel(parsePeriodKey(base!));
    return inst ? `${label} · cuota ${inst}` : label;
  } catch {
    return key;
  }
}

export type HaciendaSummary =
  | { kind: "ALL_CLEAR"; checkedAt: string }
  | { kind: "PROBLEM"; taxpayerIds: string[] }
  | { kind: "UNKNOWN" };

/** Plan §9.1 "HACIENDA STATUS": omiso / moroso across taxpayers. */
export function haciendaSummary(statuses: readonly StatusRow[], taxpayerIds: readonly string[]): HaciendaSummary {
  if (taxpayerIds.length === 0) return { kind: "UNKNOWN" };
  const byTp = new Map(statuses.map((s) => [s.taxpayer_id, s]));
  const problems = taxpayerIds.filter((id) => {
    const s = byTp.get(id);
    return s && (s.moroso === true || s.omiso === true || s.mismatch);
  });
  if (problems.length) return { kind: "PROBLEM", taxpayerIds: problems };
  const all = taxpayerIds.map((id) => byTp.get(id));
  if (all.some((s) => !s || !s.ok)) return { kind: "UNKNOWN" };
  const oldest = all.map((s) => s!.checked_at).sort()[0]!;
  return { kind: "ALL_CLEAR", checkedAt: oldest };
}
